import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SalasService {
  private readonly API = `${environment.apiUrl}/v1`;

  // Mapeamento estático para bitmask
  private static readonly DAYS_MASK: Record<string, number> = {
    seg: 1, ter: 2, qua: 4, qui: 8, sex: 16, sab: 32, dom: 64
  };

  private static readonly DAYS_ORDER = ['seg', 'ter', 'qua', 'qui', 'sex', 'sab', 'dom'] as const;
  
  private static readonly DAYS_NAMES: Record<string, string> = {
    seg: 'Segunda', ter: 'Terça', qua: 'Quarta', qui: 'Quinta', sex: 'Sexta', sab: 'Sábado', dom: 'Domingo'
  };

  constructor(private http: HttpClient) {}

  // --- CHAMADAS API ---

  getSalas(page: number, pageSize: number, filter: string): Observable<any> {
    return this.http.get(`${this.API}/rooms?page=${page}&pageSize=${pageSize}&search=${filter}`);
  }

  getUsersByRoom(roomId: string, hasPermission: boolean, page: number = 1, pageSize: number = 8, search?: string): Observable<any> {
    let url = `${this.API}/access/users-by-room-permission?roomId=${encodeURIComponent(roomId)}&hasPermission=${hasPermission}&Page=${page}&PageSize=${pageSize}`;
    if (search && search.trim()) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }
    return this.http.get<any>(url);
  }

  grantAccess(payload: any): Observable<any> {
    return this.http.post(`${this.API}/access/grant-access`, payload);
  }

  revokeAccess(userId: string, roomId: string): Observable<any> {
    return this.http.delete(`${this.API}/access/revoke-access`, { body: { userId, roomId } });
  }

  replicateRoomAccess(sourceRoomId: string, targetRoomId: string): Observable<{ replicatedCount: number; grantIds: string[]; message: string }> {
    return this.http.post<{ replicatedCount: number; grantIds: string[]; message: string }>(
      `${this.API}/access/replicate-room-access`,
      { sourceRoomId, targetRoomId }
    );
  }

  // --- REGRAS DE NEGÓCIO E AUXILIARES ---

  // Calcula o valor decimal da bitmask dos dias selecionados
  calculateDaysValue(selectedDays: string[]): number {
    return (selectedDays || [])
      .filter(d => SalasService.DAYS_MASK[d] != null)
      .reduce((sum, d) => sum + SalasService.DAYS_MASK[d], 0);
  }

  // Converte a bitmask vinda da API em nomes amigáveis para a tabela 
  daysNumberToDisplayString(days: number): string {
    if (days == null) return '';
    const names: string[] = [];
    for (const key of SalasService.DAYS_ORDER) {
      if (days & SalasService.DAYS_MASK[key]) {
        names.push(SalasService.DAYS_NAMES[key]);
      }
    }
    return names.join(', ');
  }

  //  Transforma chaves (seg, ter...) de volta a partir de um número 
  daysNumberToDayKeys(days: number): string[] {
    return SalasService.DAYS_ORDER.filter(key => !!(days & SalasService.DAYS_MASK[key]));
  }

  //  Formata tempo vindo da API (HH:mm:ss) para exibição (HH:mm) 
  formatTimeForDisplay(value: string): string {
    if (!value) return '--:--';
    const parts = value.split(':');
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
  }

  //  Prepara o tempo para envio à API (HH:mm:00) 
  formatTimeToSeconds(value: string): string {
    if (!value) return '00:00:00';
    const [h, m] = value.split(':');
    return `${h.padStart(2, '0')}:${m.padStart(2, '0')}:00`;
  }

  // Normaliza o schedule do usuário para a tabela 
  buildScheduleTableItems(schedule: any): Array<{ dia: string; startTime: string; endTime: string; _raw: any; _index: number }> {
    const list = Array.isArray(schedule) ? schedule : (schedule != null ? [schedule] : []);
    return list.map((item: any, index: number) => ({
      dia: this.daysNumberToDisplayString(item.days || item.Days || 0),
      startTime: this.formatTimeForDisplay(item.startTime || item.StartTime || ''),
      endTime: this.formatTimeForDisplay(item.endTime || item.EndTime || ''),
      _raw: item,
      _index: index
    }));
  }

  // Extrai os schedules existentes para não sobrescrever ao adicionar novo 
  getExistingSchedules(user: any): Array<any> {
    const raw = user?.schedule ?? user?.schedules ?? user?.Schedule ?? user?.Schedules;
    const list = Array.isArray(raw) ? raw : (raw != null ? [raw] : []);
    return list.map((item: any) => ({
      days: item?.days ?? item?.Days ?? 0,
      startTime: item?.startTime ?? item?.StartTime ?? '00:00:00',
      endTime: item?.endTime ?? item?.EndTime ?? '00:00:00',
      isActive: !!(item?.isActive ?? item?.IsActive ?? true)
    }));
  }

  deleteSala(id: string): Observable<any> {
    return this.http.delete(`${this.API}/rooms/${id}`);
  }

  getAccessLogsByRoom(roomId: string, page: number = 1, pageSize: number = 50): Observable<any> {
    return this.http.get<any>(`${this.API}/access/log-access?roomId=${encodeURIComponent(roomId)}&page=${page}&pageSize=${pageSize}`);
  }

  getAccessLogsByRoomAndDevice(roomId: string, deviceMacAddress: string, page: number = 1, pageSize: number = 50, startDate?: string, endDate?: string, search?: string): Observable<any> {
    let url = `${this.API}/access/log-access?roomId=${encodeURIComponent(roomId)}&deviceMacAddress=${encodeURIComponent(deviceMacAddress)}&page=${page}&pageSize=${pageSize}`;
    if (startDate) {
      url += `&startDate=${encodeURIComponent(startDate)}`;
    }
    if (endDate) {
      url += `&endDate=${encodeURIComponent(endDate)}`;
    }
    if (search && search.trim()) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }
    return this.http.get<any>(url);
  }

  getDeviceById(deviceId: string): Observable<any> {
    return this.http.get<any>(`${this.API}/devices/${deviceId}`);
  }

  getAccessLogsByDeviceMacAddress(deviceMacAddress: string, page: number = 1, pageSize: number = 50): Observable<any> {
    return this.http.get<any>(`${this.API}/access/log-access?deviceMacAddress=${encodeURIComponent(deviceMacAddress)}&page=${page}&pageSize=${pageSize}`);
  }

}